import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Magnetic from "@/components/shared/Magnetic";

const PRINCIPLES = [
  {
    n: "01",
    t: "CPM-based, priced fairly",
    d: "You pay for real, verified impressions on real creator accounts. No inflated CPMs, no fake reach, no retainer padding. Every impression is auditable back to the creator that delivered it.",
  },
  {
    n: "02",
    t: "Tell us your budget",
    d: "We reverse-engineer the campaign to fit it. Creator mix, wave count, paid layer, playlist support. Every dollar has a line item. Nothing hidden, nothing marked up on top.",
  },
  {
    n: "03",
    t: "One invoice, one team",
    d: "No middlemen, no sub-agencies, no surprise pass-through fees. The strategist you meet on day one runs the campaign to completion and reports directly to you.",
  },
  {
    n: "04",
    t: "Reporting that tracks money, not vanity",
    d: "Every report ties impressions to spend, spend to saves, saves to streams. If a lever isn't moving, we kill it and redeploy, often mid-campaign.",
  },
];

export default function HowWeBill() {
  return (
    <section
      data-testid="home-how-we-bill"
      className="relative py-12 md:py-16 border-t border-white/5 bg-black"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-6">
              How we bill
            </div>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-balance">
              Fair CPM.<br />
              <span className="italic font-light">Tell us your budget.</span>
            </h2>
            <p className="mt-6 max-w-md text-zinc-400 leading-relaxed">
              Most agencies quote a rate, mark it up, then subcontract the work.
              We don&apos;t. Prulo is priced on cost-per-thousand impressions, the way
              every performance channel should be, and every line item is written
              in the same currency as your outcome.
            </p>
            <div className="mt-10">
              <Magnetic radius={140} strength={0.4}>
                <Link
                  to="/contact"
                  data-testid="how-we-bill-cta"
                  className="inline-flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3.5 hover:bg-zinc-200 btn-magnetic glow-cta"
                >
                  Get a custom proposal <ArrowUpRight size={18} />
                </Link>
              </Magnetic>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PRINCIPLES.map((p) => (
              <div
                key={p.n}
                data-testid={`bill-principle-${p.n}`}
                className="border border-white/10 rounded-2xl p-6 md:p-7 hover-lift glow-card"
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--gx", `${e.clientX - r.left}px`);
                  e.currentTarget.style.setProperty("--gy", `${e.clientY - r.top}px`);
                }}
              >
                <div className="text-xs font-mono text-zinc-500">{p.n}</div>
                <div className="mt-3 font-display font-semibold text-xl">{p.t}</div>
                <div className="mt-3 text-sm text-zinc-400 leading-relaxed">{p.d}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-sm text-zinc-500 max-w-2xl">
            Every proposal is written by hand for the release in front of us.
            No packages, no tiers, no fine print.
          </div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">
            No retainers, no lock-in, no mystery fees
          </div>
        </div>
      </div>
    </section>
  );
}
